// src/components/Dashboard/components/Tooltip.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';

/**
 * Reusable Tooltip Component
 * Supports hover and click triggers
 */

const Tooltip = ({ 
  content, 
  children, 
  position = 'top', // top, bottom, left, right
  trigger = 'hover', // hover, click
  delay = 200,
  maxWidth = '200px'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const showTooltip = () => {
    if (trigger === 'hover') {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
        calculatePosition();
      }, delay);
    } else {
      setIsVisible(!isVisible);
      calculatePosition();
    }
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (trigger === 'hover') {
      setIsVisible(false);
    }
  };

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + gap;
        break;
      default:
        break;
    }

    // Keep tooltip within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (left < 10) left = 10;
    if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10;
    }
    if (top < 10) top = 10;
    if (top + tooltipRect.height > viewportHeight - 10) {
      top = viewportHeight - tooltipRect.height - 10;
    }

    setCoords({ top, left });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (trigger === 'click' && 
          triggerRef.current && 
          !triggerRef.current.contains(e.target) &&
          tooltipRef.current &&
          !tooltipRef.current.contains(e.target)) {
        setIsVisible(false);
      }
    };

    if (isVisible && trigger === 'click') {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, trigger]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={trigger === 'hover' ? showTooltip : undefined}
        onMouseLeave={trigger === 'hover' ? hideTooltip : undefined}
        onClick={trigger === 'click' ? showTooltip : undefined}
        className="inline-block"
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-[10001] pointer-events-none"
          style={{
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            maxWidth
          }}
        >
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl px-3 py-2">
            <div className="text-xs text-gray-700 dark:text-gray-200 leading-relaxed">
              {content}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Info Tooltip - Quick wrapper for info icons with tooltips
export const InfoTooltip = ({ content, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  return (
    <Tooltip content={content}>
      <button className="inline-flex text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300 transition-colors">
        <Info className={sizeClasses[size]} />
      </button>
    </Tooltip>
  );
};

// Disclaimer Tooltip - Styled for disclaimers and warnings
export const DisclaimerTooltip = ({ title, content, children }) => {
  return (
    <Tooltip
      content={
        <div>
          {title && <div className="font-semibold mb-1 text-amber-600 dark:text-amber-300">{title}</div>}
          <div className="text-gray-600 dark:text-gray-300">{content}</div>
        </div>
      }
      maxWidth="300px"
    >
      {children || (
        <button className="inline-flex text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300 transition-colors">
          <Info className="w-4 h-4" />
        </button>
      )}
    </Tooltip>
  );
};

export default Tooltip;